import { toast } from "sonner";
import { X, Link2, Mail, MessageSquareText, Share2, Instagram, Facebook, Linkedin, Twitter, Phone } from "lucide-react";

export function ShareModal({ open, onClose, url, storyUrl, title = "My WriterHaus HausCard", text = "Hear my songs on WriterHaus — let's write together." }) {
  if (!open) return null;
  const enc = encodeURIComponent;
  const popup = (href) => window.open(href, "_blank", "noopener,width=680,height=640");

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard");
    } catch {
      toast(`Copy this link: ${url}`);
    }
  };
  const instagram = async () => {
    // Instagram has no web share intent — best flow: download a ready-to-post
    // story image + copy the link for the link sticker.
    if (storyUrl) {
      const a = document.createElement("a");
      a.href = storyUrl;
      a.download = "";
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
    window.open("https://www.instagram.com/", "_blank", "noopener");
    try {
      await navigator.clipboard.writeText(url);
      toast.success(storyUrl
        ? "Story image downloaded & link copied — post the image to your story and add the link with a link sticker"
        : "Link copied — paste it into your Instagram story, bio or DM");
    } catch {
      toast(`Copy this link for Instagram: ${url}`);
    }
  };
  const native = async () => {
    try {
      if (navigator.share) { await navigator.share({ title, text, url }); return; }
      await copy();
    } catch { /* cancelled */ }
  };

  const big = [
    { label: "Instagram", icon: Instagram, onClick: instagram, testid: "share-instagram" },
    { label: "Facebook", icon: Facebook, onClick: () => popup(`https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`), testid: "share-facebook" },
    { label: "LinkedIn", icon: Linkedin, onClick: () => popup(`https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`), testid: "share-linkedin" },
  ];
  const small = [
    { label: "X", icon: Twitter, onClick: () => popup(`https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(url)}`), testid: "share-x" },
    { label: "WhatsApp", icon: Phone, onClick: () => popup(`https://wa.me/?text=${enc(`${text} ${url}`)}`), testid: "share-whatsapp" },
    { label: "Email", icon: Mail, onClick: () => { window.location.href = `mailto:?subject=${enc(title)}&body=${enc(`${text}\n\n${url}`)}`; }, testid: "share-email" },
    { label: "Text", icon: MessageSquareText, onClick: () => { window.location.href = `sms:?&body=${enc(`${text} ${url}`)}`; }, testid: "share-sms" },
    { label: "Copy link", icon: Link2, onClick: copy, testid: "share-copy" },
    { label: "More…", icon: Share2, onClick: native, testid: "share-native" },
  ];

  return (
    <div className="fixed inset-0 z-[100] bg-ink/85 backdrop-blur-sm flex items-center justify-center p-5" data-testid="share-modal" onClick={onClose}>
      <div className="w-full max-w-md bg-surface border border-line p-6 md:p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-1">
          <h3 className="font-heading text-2xl font-bold uppercase tracking-tight text-cream">Share this HausCard</h3>
          <button onClick={onClose} data-testid="share-modal-close" className="text-ash hover:text-gold transition-colors p-1 -mr-1" aria-label="Close">
            <X size={20} />
          </button>
        </div>
        <p className="text-ash text-sm mb-6">The link opens a preview card with songs anyone can hear.</p>

        <div className="grid grid-cols-3 gap-px bg-line border border-line mb-3">
          {big.map((b) => (
            <button key={b.label} onClick={b.onClick} data-testid={b.testid} className="bg-ink flex flex-col items-center gap-3 py-7 text-cream hover:text-gold transition-colors">
              <b.icon size={28} className="text-gold" />
              <span className="font-mono text-[10px] uppercase tracking-wide">{b.label}</span>
            </button>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-px bg-line border border-line mb-6">
          {small.map((b) => (
            <button key={b.label} onClick={b.onClick} data-testid={b.testid} className="bg-surface flex flex-col items-center gap-2 py-4 text-ash hover:text-gold transition-colors">
              <b.icon size={18} />
              <span className="font-mono text-[10px] uppercase tracking-wide">{b.label}</span>
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2 border border-line bg-ink px-3 py-2.5">
          <span data-testid="share-url" className="flex-1 truncate font-mono text-[11px] text-ash">{url}</span>
          <button onClick={copy} className="shrink-0 font-mono text-[10px] uppercase tracking-wide text-gold hover:text-gold-hi transition-colors">Copy</button>
        </div>
      </div>
    </div>
  );
}
